import React, { useCallback, useEffect, useRef, useState } from "react";
import { Hub } from "aws-amplify/utils";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getAnalytics, logEvent, setUserId } from "firebase/analytics";
import { CONSENT_EVENT, hasConsent } from "../../Utils/cookieConsent";
import dayjs from "dayjs";
import {
  useAuthState,
  useAuthDispatch,
  checkUser,
  loginSuccess,
  logoutSuccess,
  requestGetUser,
  requestUpdateUser,
  ConsultantProvider,
  newFirebaseMessage,
  useConsultationDispatch,
  requestListNotification,
  useNotificationDispatch,
  useConsultationState,
  addLocalNotification,
  setCallDialog,
  SubscriptionProvider,
  AccountingProvider,
  WebinarProvider,
  TutoringProgramProvider,
  BusinessInvitationProvider,
  BusinessProductProvider,
  ResourceProvider,
  BusinessServiceProvider,
  TermsProvider,
  updateFirebaseMessage,
} from "../../Context";
import { BusinessTransactionProvider } from "../../Context/businessTransaction/context";
import { EbillingProvider } from "../../Context/ebilling/context";
import { TransactionProvider } from "../../Context/transaction/context";
import { getFirebaseToken, subscribeToMessages } from "../../firebase";
import CallModal from "../CallModal";
import { Notification } from "../Notification";
import { BusinessTaxProvider } from "../../Context/tax/context";
import { BusinessFixedAssetProvider } from "../../Context/fixedAsset/context";

const initialTimestamp = Math.floor(Date.now() / 1000);

const RequireAuth = ({ children, required = false }) => {
  const { user } = useAuthState();
  const authDispatch = useAuthDispatch();
  const { id } = useParams();

  const consultationDispatch = useConsultationDispatch();
  const { consultation, messages, ongoingCall, callDialog } =
    useConsultationState();

  const notificationDispatch = useNotificationDispatch();

  const location = useLocation();

  const [callId, setcallId] = useState(null);
  const [callTitle, setcallTitle] = useState("");
  const [callBody, setcallBody] = useState("");
  const [callTimestamp, setcallTimestamp] = useState(initialTimestamp);
  const [showCall, setshowCall] = useState(false);

  useEffect(() => {
    checkUser(authDispatch);

    // Hub.listen returns an unsubscribe function. Discarding it left a live
    // listener behind on every mount.
    const unsubscribeHub = Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signedIn":
          const lastUsedAuth = localStorage.getItem("lastUsedAuth");
          if (!lastUsedAuth) {
            localStorage.removeItem("lastUsedAuth");
            localStorage.removeItem("authProvider");
          } else {
            localStorage.setItem("authProvider", lastUsedAuth);
          }
          // Logged here, on a completed sign-in, rather than on the login
          // buttons: a click also counts failed passwords and abandoned
          // Google/Apple redirects.
          logEvent(getAnalytics(), "login", {
            method: lastUsedAuth || "unknown",
          });
          loginSuccess(authDispatch, data);
          break;
        case "signedOut":
          logoutSuccess(authDispatch);
          break;
        case "signIn_failure":
          break;
        default:
          break;
      }
    });

    return unsubscribeHub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.username) {
      requestGetUser(authDispatch, { sub: user.username });
    }
  }, [authDispatch, user.username]);

  // prevPage sends the user back to the page that required login. Once they
  // are signed in and on that page, drop it so a later login lands on the
  // dashboard instead of an old destination.
  useEffect(() => {
    if (
      user.username &&
      localStorage.getItem("prevPage") === location.pathname + location.search
    ) {
      localStorage.removeItem("prevPage");
    }
  }, [user.username, location.pathname, location.search]);

  // Firebase notification & analytics
  useEffect(() => {
    if (user.id) {
      getFirebaseToken()
        .then((token) => {
          if (user.notificationToken?.web !== token) {
            requestUpdateUser(authDispatch, {
              input: {
                notificationToken: {
                  web: token,
                },
              },
            });
          }
        })
        .catch((err) => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  // GA4 user id only with analytics consent, also when consent arrives after
  // login. Without it the user id would tie cookieless hits to the person.
  useEffect(() => {
    if (!user.id) return undefined;
    const syncUserId = () => {
      setUserId(getAnalytics(), hasConsent("analytics") ? user.id : null);
    };
    syncUserId();
    window.addEventListener(CONSENT_EVENT, syncUserId);
    return () => window.removeEventListener(CONSENT_EVENT, syncUserId);
  }, [user.id]);

  const isInConsultationPage = (consultationId) => {
    return (
      location.pathname === "/consultation" ||
      location.pathname === `/chat/${consultationId}` ||
      location.pathname === `/v2/chat/${consultationId}` ||
      location.pathname === `/v3/chat/${consultationId}`
    );
  };

  const handleNewMessage = (payload) => {
    let alreadyExist = false;
    if (payload?.data?.id && payload?.data?.item) {
      const item = JSON.parse(payload.data.item);
      if (consultation?.id === payload.data.id) {
        const messageId = item.id;
        const messageExists = messages.some(
          (message) => message.id === messageId,
        );

        if (messageExists) {
          alreadyExist = true;
        } else if (!isInConsultationPage(payload.data.id)) {
          addLocalNotification(notificationDispatch, {
            id: messageId,
            type: payload.data.type,
            title: payload.notification.title,
            body: payload.notification.body,
          });
        }
      }
    }

    if (!alreadyExist) {
      newFirebaseMessage(consultationDispatch, payload);
    }
  };

  // Recreated every render so it always sees current state, but never
  // re-subscribes — the subscription below reaches it through a ref.
  const handleFirebaseMessage = useCallback(
    (payload) => {
      const messageType = payload?.data?.type;

      switch (messageType) {
        case "call_end":
          if (payload.data.id === callId) {
            setcallId(null);
            setshowCall(false);
          }
          break;

        case "call":
          // Ignore calls addressed to a different user. The browser FCM token
          // is device-bound, so a stale token (previous logged-in user) can
          // still receive a call meant for them. The webhook now stamps the
          // recipient userId — only show the modal if it matches.
          if (payload.data.userId && payload.data.userId !== user.id) {
            break;
          }
          if (ongoingCall === "") {
            if (callDialog) {
              setCallDialog(consultationDispatch, { callDialog: false });
            }
            setcallId(payload.data.id);
            setcallTitle(payload.notification.title);
            setcallBody(payload.notification.body);
            setcallTimestamp(Math.floor(Date.now() / 1000));
            setshowCall(true);
          }
          break;

        case "message":
          handleNewMessage(payload);
          break;

        case "edit_message":
          updateFirebaseMessage(consultationDispatch, payload);
          break;

        default:
          addLocalNotification(notificationDispatch, {
            id: payload.data.id,
            type: payload.data.type,
            title: payload.notification.title,
            body: payload.notification.body,
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      callId,
      user.id,
      ongoingCall,
      callDialog,
      consultation?.id,
      messages,
      location.pathname,
    ],
  );

  const messageHandlerRef = useRef(handleFirebaseMessage);

  useEffect(() => {
    messageHandlerRef.current = handleFirebaseMessage;
  }, [handleFirebaseMessage]);

  // One FCM subscription for the lifetime of the mount. This used to run in
  // the render body, registering a fresh onMessage handler on every render.
  useEffect(() => {
    return subscribeToMessages((payload) => messageHandlerRef.current?.(payload));
  }, []);

  useEffect(() => {
    if (location.pathname !== "/notification" && user.id) {
      const startOfLastHour = dayjs().subtract(1, "hour").toISOString();
      const endOfLastHour = dayjs().toISOString();
      requestListNotification(notificationDispatch, {
        page: 1,
        limit: 5,
        owner: user.id,
        createdAt: {
          between: [startOfLastHour, endOfLastHour],
        },
      });
    }
  }, [notificationDispatch, location.pathname, user.id]);

  if (location.pathname === "/") {
    return <Navigate to="/dashboard" replace />;
  } else if (!user.username && required) {
    const prevPage = location.pathname + location.search;
    localStorage.setItem("prevPage", prevPage);
    return <Navigate to="/login" replace />;
  } else if (
    (location.pathname === "/login" ||
      location.pathname === "/login/" ||
      location.pathname === "/passwordless-login" ||
      location.pathname === "/passwordless-login/" ||
      location.pathname === "/verify" ||
      location.pathname === "/verify/" ||
      location.pathname === "/signup" ||
      location.pathname === "/signup/" ||
      location.pathname === "/passwordless-signup" ||
      location.pathname === "/passwordless-signup/" ||
      location.pathname === "/confirm" ||
      location.pathname === "/confirm/" ||
      location.pathname === "/reset" ||
      location.pathname === "/reset/") &&
    user.username
  ) {
    const prevPage = localStorage.getItem("prevPage");
    return <Navigate to={prevPage ? prevPage : "/dashboard"} replace />;
  } else if (user.id && user.membership !== "CONSULTANT") {
    if (
      [
        "/manage-consultation",
        "/consultant-transaction",
        "/consultant-withdraw",
      ].includes(location.pathname)
    ) {
      return <Navigate to="/consultation" replace />;
    } else if (
      ["/manage-tutoring", "/manage-tutoring/create"].includes(
        location.pathname,
      )
    ) {
      return <Navigate to="/tutoring" replace />;
    } else if ([`/manage-tutoring/edit/${id}`].includes(location.pathname)) {
      return <Navigate to={`/tutoring/detail/${id}`} replace />;
    } else if (location.pathname === `/manage-business-service/${id}`) {
      return <Navigate to={`/business-service/${id}`} replace />;
    } else if (location.pathname === `/manage-business-service/edit/${id}`) {
      return <Navigate to={`/business-service/form/${id}`} replace />;
    } else if (location.pathname.startsWith("/manage-business-service")) {
      return <Navigate to="/business-service" replace />;
    }
  }

  if (required) {
    return (
      <>
        <Notification />
        <CallModal
          show={
            showCall &&
            callId !== null &&
            !location.pathname.startsWith("/v2/chat/") &&
            !location.pathname.startsWith("/v3/chat/")
          }
          callId={callId}
          title={callTitle}
          body={callBody}
          timestamp={callTimestamp}
          onClose={() => {
            setcallId(null);
            setcallTitle("");
            setcallBody("");
            setshowCall(false);
          }}
        />
        <TermsProvider>
          <BusinessServiceProvider>
            <ResourceProvider>
              <BusinessInvitationProvider>
                <BusinessProductProvider>
                  <BusinessTaxProvider>
                    <BusinessFixedAssetProvider>
                      <AccountingProvider>
                      <TutoringProgramProvider>
                        <WebinarProvider>
                          <SubscriptionProvider>
                            <BusinessTransactionProvider>
                              <TransactionProvider>
                                <ConsultantProvider>
                                  <EbillingProvider>
                                    {children}
                                  </EbillingProvider>
                                </ConsultantProvider>
                              </TransactionProvider>
                            </BusinessTransactionProvider>
                          </SubscriptionProvider>
                        </WebinarProvider>
                      </TutoringProgramProvider>
                      </AccountingProvider>
                    </BusinessFixedAssetProvider>
                  </BusinessTaxProvider>
                </BusinessProductProvider>
              </BusinessInvitationProvider>
            </ResourceProvider>
          </BusinessServiceProvider>
        </TermsProvider>
      </>
    );
  }

  return (
    <AccountingProvider>
      <BusinessTransactionProvider>
        {children}
      </BusinessTransactionProvider>
    </AccountingProvider>
  );
};

export default RequireAuth;
